import { DataType, EditorTag, utils as dataUtils } from '@easydata/core';
import { Expression } from './expression';
import { DataKind } from '../types/data_kind';
import { LinkType } from '../types/link_type';
import { CondTag } from '../types/cond_tag';
import { ExprTag } from '../types/expr_tag';
import { QueryChangePart, QueryChangeAction } from './query_events';
import { EqValueEditor } from '../dm/value_editor';
import { EqEditorTag } from '../types/eq_editor_tag';
import { ConditionPart } from '../types/cond_part';
/**
 * Represents some condition.
 */
var Condition = /** @class */ (function () {
    /**
     * The default constructor.
     * @param tag The condition tag
     */
    function Condition(query, tag) {
        this.query = query;
        /**
         *  The linking type.
         */
        this.linkType = LinkType.All; //only for predicates
        this.parent = null;
        this._id = dataUtils.generateId('cond');
        this.justAdded = false;
        this.tag = tag || CondTag.Unknown;
        this.enabled = true;
        this.operatorId = '';
        this.blockId = '';
        this.expressions = new Array();
        this.conditions = new Array();
    }
    Object.defineProperty(Condition.prototype, "id", {
        get: function () {
            return this._id;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Condition.prototype, "enabled", {
        get: function () {
            return this._enabled && (!this.getParent() || this.getParent().enabled);
        },
        set: function (value) {
            this._enabled = value && (!this.getParent() || this.getParent().enabled);
            this.conditions && this.conditions.forEach(function (cond) {
                cond.enabled = value;
            });
        },
        enumerable: true,
        configurable: true
    });
    Condition.prototype.getQuery = function () {
        return this.query;
    };
    Condition.prototype.getModel = function () {
        return this.query.getModel();
    };
    Condition.prototype.getParent = function () {
        return this.parent;
    };
    Condition.prototype.setParent = function (parent) {
        this.parent = parent;
    };
    Condition.prototype.getConditions = function () {
        return this.conditions;
    };
    Condition.prototype.isEmpty = function () {
        return this.conditions.length === 0;
    };
    Condition.prototype.isGroup = function () {
        return (this.tag == CondTag.Group) || (this.parent == null);
    };
    Condition.prototype.getLevel = function () {
        var parent = this.getParent();
        return parent ? parent.getLevel() + 1 : 0;
    };
    /**
     * Indicates whether this condition is read-only.
     */
    Condition.prototype.isReadOnly = function () {
        return false;
    };
    Condition.prototype.setReadOnly = function (value) {
    };
    /**
     * The value indicating whether this condition is parameterized.
     */
    Condition.prototype.isParameterized = function () {
        return false;
    };
    Condition.prototype.setParameterized = function (value) {
    };
    /**
     * Indicates whether this condition must be placed into `JOIN` clause in result `SQL`.
     */
    Condition.prototype.isInJoin = function () {
        return false;
    };
    Condition.prototype.setInJoin = function (value) {
    };
    Condition.prototype.clearConditions = function () {
        this.conditions = [];
    };
    Condition.prototype.addCondition = function (cond) {
        cond.setParent(this);
        return this.conditions.push(cond);
    };
    Condition.prototype.removeConditionAt = function (index) {
        this.conditions.splice(index, 1);
    };
    /**
    * Loads condtion from its JSON representation object.
    * @param model The Data Model.
    * @param data The JSON representation object.
    */
    Condition.prototype.loadFromData = function (model, data) {
        if (data) {
            this.tag = data.tag;
            if (!this.tag) {
                this.tag = (!this.parent || this.conditions.length > 0) ? CondTag.Group : CondTag.Simple;
            }
            if (typeof data.enabled !== 'undefined') {
                this.enabled = data.enabled;
            }
            if (typeof data.disabled !== 'undefined') {
                this.enabled = !data.disabled;
            }
            if (this.tag == CondTag.Simple) {
                this.operatorId = data.op;
                if (data.exprs) {
                    for (var i = 0; i < data.exprs.length; i++) {
                        var newExpr = new Expression(this);
                        newExpr.loadFromData(model, data.exprs[i]);
                        this.expressions.push(newExpr);
                    }
                }
            }
            this.linkType = data.linking;
        }
    };
    /**
     * Saves condition to JSON representation object.
     * @returns The JSON representation object.
     */
    Condition.prototype.saveToData = function () {
        var obj = {};
        obj.tag = this.tag;
        if (!this.enabled) {
            obj.enabled = this.enabled;
        }
        if (this.tag == CondTag.Simple) {
            obj.op = this.operatorId;
            obj.exprs = [];
            for (var i = 0; i < this.expressions.length; i++) {
                obj.exprs.push(this.expressions[i].saveToData());
            }
        }
        if (this.tag == CondTag.Group) {
            //if condition group
            obj.linking = this.linkType;
        }
        return obj;
    };
    Condition.prototype.fireChangedEvent = function (condPart) {
        if (condPart === void 0) { condPart = ConditionPart.All; }
        this.query.fireChangedEvent({
            part: QueryChangePart.Conditions,
            action: QueryChangeAction.Modify,
            changee: this,
            condPart: condPart
        });
    };
    Condition.prototype.getExpressionIndex = function (expr) {
        return this.expressions.indexOf(expr, 0);
    };
    Condition.prototype.expressionChanged = function (expr, oldValue) {
        if (expr.getIndex() == 0 && expr.tag == ExprTag.EntityAttribute) {
            //this is a base entity attribute, we need to re-pack the whole condition
            this.tuneOperatorForAttr(expr.value);
        }
    };
    Condition.prototype.getOperatorId = function () {
        return this.operatorId;
    };
    Condition.prototype.setOperatorId = function (newOperatorId, silent) {
        if (silent === void 0) { silent = false; }
        var oldOperatorId = this.operatorId;
        this.operatorId = newOperatorId;
        if (!silent) {
            var model = this.getModel();
            var operator = model.getOperatorById(newOperatorId);
            var attrId = this.expressions[0].value;
            var attr = model.getAttributeById(attrId);
            this.tuneValueExpressions(attr, operator, attr.id, oldOperatorId);
        }
    };
    Condition.prototype.tuneOperatorForAttr = function (oldAttrId) {
        var model = this.getModel();
        var attrId = this.expressions[0].value;
        var attr = model.getAttributeById(attrId);
        if (attr != null) {
            var newOperator = this.getModel().getDefaultOperatorForAttr(attr);
            var oldOperatorId = this.operatorId;
            this.operatorId = newOperator.id;
            this.tuneValueExpressions(attr, newOperator, oldAttrId, oldOperatorId);
        }
    };
    Condition.prototype.tuneValueExpressions = function (attr, operator, oldAttrId, oldOperatorId) {
        var model = this.getModel();
        if (!operator)
            return;
        var exprCount = this.expressions.length;
        var oldAttr = oldAttrId ? model.getAttributeById(oldAttrId) : null;
        var oldOperator = null;
        if (oldOperatorId) {
            oldOperator = model.getOperatorById(oldOperatorId);
        }
        if (oldOperator && oldOperator.paramCount == operator.paramCount) {
            //add  necessary expressions
            var i = 1;
            while (i < operator.paramCount) {
                var oldOperand = model.getOperand(attr, oldOperator, i);
                var operand = model.getOperand(attr, operator, i);
                if (operand.dataType == DataType.Unknown) {
                    operand.dataType = attr.dataType;
                }
                var editor = operand.editor && operand.editor.tag !== EditorTag.Unknown
                    ? operand.editor
                    : (attr.defaultEditor && attr.defaultEditor.tag !== EditorTag.Unknown
                        ? attr.defaultEditor
                        : new EqValueEditor());
                if (i >= exprCount) {
                    var valExpr = this.createValueExpr(operand);
                    this.expressions.push(valExpr);
                }
                else {
                    var oldExpr = this.expressions[i];
                    //create new value expression according to the corresponding operand
                    var newExpr = this.createValueExpr(operand);
                    if (this.areCompitableEditors(oldOperand.editor, editor)) {
                        newExpr.tryCopyValueFrom(oldExpr);
                    }
                    this.expressions[i] = newExpr;
                }
                i++;
            }
            if (exprCount > i) {
                //remove all redundant expressions
                this.expressions.splice(i, exprCount - i);
            }
        }
        else {
            this.expressions.splice(1, this.expressions.length - 1);
            for (var i = 1; i < operator.paramCount; i++) {
                var operand = model.getOperand(attr, operator, i);
                if (operand.dataType == DataType.Unknown) {
                    operand.dataType = attr.dataType;
                }
                var valExpr = this.createValueExpr(operand);
                this.expressions.push(valExpr);
            }
        }
    };
    Condition.prototype.areCompitableEditors = function (oldEditor, newEditor) {
        if (newEditor.tag == EqEditorTag.CustomList || newEditor.tag == EqEditorTag.List
            || newEditor.tag == EqEditorTag.SqlList || newEditor.tag == EditorTag.Unknown) {
            return false;
        }
        return true;
    };
    Condition.prototype.addExpressionByOperand = function (operand, val) {
        if (val === void 0) { val = null; }
        var expr = this.createValueExpr(operand, val);
        this.expressions.push(expr);
        return expr;
    };
    Condition.prototype.createValueExpr = function (operand, val) {
        if (val === void 0) { val = null; }
        var valueNotDefined = val === null || typeof val === 'undefined';
        var value = val ? val : operand.defValue;
        var text = val ? val : operand.defText;
        var result = new Expression(this);
        if (operand.kind === DataKind.Query) {
            result.tag = ExprTag.Query;
            result.dataType = operand.dataType;
            result.kind = operand.kind;
            result.setContent(value, text, true);
        }
        else if (operand.kind === DataKind.Attribute) {
            if (!valueNotDefined) {
                var defAttr = this.getModel().getFirstAttributeByFilter(function (attr) {
                    return attr.useInConditions == true && (!operand.dataType || attr.dataType == operand.dataType);
                });
                value = defAttr ? defAttr.id : null;
            }
            result.tag = ExprTag.EntityAttribute;
            result.kind = operand.kind;
            result.dataType = operand.dataType;
            result.setContent(value, text, true);
        }
        else {
            if (valueNotDefined) {
                if (operand.defValue && operand.defValue != "") {
                    value = operand.defValue;
                }
                else {
                    if (operand.editor && operand.editor.defValue) {
                        value = operand.editor.defValue;
                    }
                    else {
                        value = '';
                    }
                }
                if (operand.editor) {
                    text = operand.editor.getValueText(value);
                }
            }
            result.tag = ExprTag.Constant;
            result.dataType = operand.dataType;
            result.kind = operand.kind;
            result.setContent(value, text, true);
            result._isDefaultValue = valueNotDefined;
        }
        return result;
    };
    return Condition;
}());
export { Condition };
//# sourceMappingURL=condition.js.map